import { memo } from 'react';
import type {
    CourseOverviewLearningUnitFragment,
    CourseOverviewLessonFragment,
} from '@lingoda/monolith-graphql';
import { StudentStatus } from '@lingoda/monolith-graphql';
import { trans } from '@lingoda/i18n';
import { RouterLink, studentLessonDetailsPath } from '@lingoda/router';
import {
    Box,
    Chip,
    Collapse,
    Divider,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
    useTheme,
} from '@lingoda/ui';
import { colorsPalette } from '@lingoda/ui/theme';
import { LessonStatusTooltip } from './LessonStatusTooltip';
import Progress from './Progress';
import VideoCall from './VideoCall.svg?component';
import { ChapterFlashcardsContent } from './ChapterFlashcardsContent';

interface Props {
    learningUnit: CourseOverviewLearningUnitFragment;
    completed: number;
    isOpen: boolean;
    lessons: ReadonlyArray<CourseOverviewLessonFragment>;
    onClick: () => void;
    total: number;
    moduleId: number;
}

const LearningUnitContentComponent = ({
    learningUnit,
    completed,
    isOpen,
    lessons,
    onClick,
    total,
}: Props) => {
    const theme = useTheme();

    return (
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <List disablePadding>
                {learningUnit.vocabularyItems.length > 0 && (
                    <ListItem divider disablePadding>
                        <Box sx={{ width: '100%' }}>
                            <ChapterFlashcardsContent learningUnit={learningUnit} />
                        </Box>
                    </ListItem>
                )}
                <ListItem>
                    <ListItemIcon>
                        <VideoCall color={theme.palette.text.primary} />
                    </ListItemIcon>
                    <ListItemText disableTypography>
                        <Typography variant="body1">
                            {trans('live-lessons', {}, 'student-common')}
                        </Typography>
                    </ListItemText>
                    <Progress completed={completed} total={total} />
                </ListItem>
                {lessons.map(
                    (lesson) =>
                        lesson && (
                            <ListItemButton
                                key={lesson.id}
                                component={RouterLink}
                                onClick={onClick}
                                color="inherit"
                                to={studentLessonDetailsPath({
                                    lessonId: lesson.id,
                                    isForNewClassroom: undefined,
                                })}
                                sx={{
                                    backgroundColor:
                                        lesson.studentStatus === StudentStatus.Skip
                                            ? colorsPalette.blackAlpha[4]
                                            : '',
                                }}
                            >
                                <ListItemIcon>
                                    <LessonStatusTooltip lesson={lesson} />
                                </ListItemIcon>
                                <ListItemText primary={lesson.title} />
                                <Chip color="primary" label={lesson.label} />
                            </ListItemButton>
                        ),
                )}
            </List>
            <Divider />
        </Collapse>
    );
};

export const LearningUnitContent = memo(LearningUnitContentComponent);
